import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Select, Spin } from 'antd';
import UserGroupRowDisplay from '../user_groups_index_page/UserGroupRowDisplay';
import { debounce } from '../../utils/PerformanceUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { fetchUserGroupsForPicker } from '../../thunks/user_groups_thunks';
import { USER_GROUPS_PICKER_TARGET_COLLECTION } from '../../types/user_groups';
import { withUserType } from '../withUserType';

const SEARCH_DEBOUNCE_MS = 300;

// Multi-select of the tenant's user groups, for the invite / edit user modals. The value is a
// list of group ids, sent as `userGroupIds` on the contact write the modal submits.
//
// A plain controlled select: `UserGroupsFormItem` does the initial reads and seeds the value, and
// this only issues a request of its own when the admin types a search. Options come from the slice's
// `records` rather than the picker collection's ids on purpose: `records` accumulates everything
// fetched (the first page, every search, and the edited user's own groups), so a group stays
// labelled after a search narrows the list away from it.
const UserGroupSelect: FC<any> = ({
	value,
	onChange,
	placeholder = 'Select user groups',
	userGroups,
	fetchGroups,
}): React.ReactElement => {
	const [searchText, setSearchText] = useState('');

	const records = nullSafeGetOrElse('records', userGroups, {});
	const loading = nullSafeGetOrElse(
		`${USER_GROUPS_PICKER_TARGET_COLLECTION}.fetching`,
		userGroups,
		false
	);

	// A tenant can hold more groups than the one page the prefetch asked for, so typing also
	// asks the server (the `search` filter is a name LIKE). Results merge into `records`.
	const searchOnServer = useMemo(
		() =>
			debounce((term) => {
				fetchGroups(term).catch(() => {});
			}, SEARCH_DEBOUNCE_MS),
		[fetchGroups]
	);

	const onSearch = useCallback(
		(term) => {
			setSearchText(term);
			if (term) {
				searchOnServer(term);
			}
		},
		[searchOnServer]
	);

	// antd clears its own search box on select (`autoClearSearchValue` defaults to true), but
	// the visible list is filtered off local `searchText` — without clearing that too the list
	// would stay filtered by a term the user can no longer see.
	const handleChange = useCallback(
		(nextValue) => {
			setSearchText('');
			if (onChange) {
				onChange(nextValue);
			}
		},
		[onChange]
	);

	const visibleGroups = useMemo(() => {
		const term = searchText.trim().toLowerCase();
		return Object.keys(records)
			.map((recordId) => records[recordId])
			.filter(
				(group) => !term || nullSafeGetOrElse('name', group, '').toLowerCase().indexOf(term) !== -1
			)
			.sort((left, right) =>
				nullSafeGetOrElse('name', left, '').localeCompare(nullSafeGetOrElse('name', right, ''))
			);
	}, [records, searchText]);

	return (
		<Select
			mode="multiple"
			value={value}
			onChange={handleChange}
			placeholder={placeholder}
			style={{ width: '100%' }}
			// Filtering happens against `visibleGroups` above, so antd must not filter again.
			filterOption={false}
			onSearch={onSearch}
			// Keeps the selected tags compact — otherwise antd renders the whole icon/name row
			// inside every tag.
			optionLabelProp="label"
			notFoundContent={loading ? <Spin size="small" /> : 'No user groups found'}
			loading={loading}
		>
			{visibleGroups.map((group) => (
				<Select.Option
					key={nullSafeGet('id', group)}
					value={nullSafeGet('id', group)}
					label={nullSafeGetOrElse('name', group, '')}
				>
					<UserGroupRowDisplay record={group} />
				</Select.Option>
			))}
		</Select>
	);
};

const mapStateToProps = (state) => ({
	userGroups: state.user_groups,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchGroups: (search = null) => dispatch(fetchUserGroupsForPicker(ownProps.userType)(search)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(UserGroupSelect));
